import React from 'react';
import { Star, Quote, Play, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

const testimonials = [
  {
    name: "Femina Salon",
    // business: "Elite Hair Studio, Miami",
    text: "Zylu has proven to be a valuable asset to my business. Over the past 4 months, their referral program has consistently brought in new customers. I am highly satisfied with their billing software. I would confidently recommend Zylu to business owner seeking reliable and effective solutions",
    rating: 5
  },
  {
    name: "Toni & Guy",
    // business: "Zen Spa & Wellness, Austin",
    text: "In just one month, the billing software has made managing finances much easier compared to our previous solution. The app is user-friendly, and my employees have quickly adapted to it, increasing their efficiency. Zylu has simplified our operations, and I highly recommend it for its ease of use and employee management.",
    rating: 5
  },
  {
    name: "IFA Salon",
    text:"The software is incredibly user-friendly, allowing me to manage operations effortlessly. The loyalty program has been a hit with customers, helping them earn points and rewards. The billing software is seamless and efficient, making transactions a breeze. I am highly satisfied with Zylu and it will be really helpful for salon owner seeking a reliable and user-friendly solution.",
    rating: 5
  },
  {
    name: "Lavish Family Salon",
    text:"I have been using Zylu for six months now, The software has been an excellent tool for managing my billing process and customer data. It has also been a fantastic asset to my marketing efforts, allowing me to connect with customers more effectively.",
    rating: 5
  }
];

const videoTestimonials = [
  {
    id: 's1xjd9mBky4',
    title: 'Hairport Unisex Salon',
    clientName: 'SIYA - MANAGER',
    business: 'RR NAGAR, BENGALURU, INIDA',
    thumbnail: `https://img.youtube.com/vi/s1xjd9mBky4/maxresdefault.jpg`
  },
  {
    id: '0QLqXlE3s_0',
    title: 'Toni & Guy',
    clientName: 'LALLITHA - MANAGER',
    business: 'SAHAKARNAGAR, BENGALURU, INIDA',
    thumbnail: `https://img.youtube.com/vi/0QLqXlE3s_0/maxresdefault.jpg`
  },
  {
    id: 'M29At8OmZEo',
    title: 'Studio 89 Unisex Salon',
    clientName: 'CHAITRA - MANAGER',
    business: 'BENGALURU, INIDA',
    thumbnail: `https://img.youtube.com/vi/M29At8OmZEo/maxresdefault.jpg`
  }
];

const Testimonials: React.FC = () => {
  const [activeVideo, setActiveVideo] = React.useState<string | null>(null);

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-black mb-6">
            What Salon Owners Are Saying About Zylu
          </h2>
          <p className="text-xl text-gray-600">
            Real results from real salon owners who made the switch
          </p>
        </div>

        <div className="grid md:grid-cols-3 gap-8 mb-16">
          {testimonials.map((testimonial, index) => (
            <div key={index} className="bg-white p-8 rounded-xl shadow-lg hover:shadow-xl transition-shadow">
              <div className="flex items-center mb-6">
                <Quote className="h-8 w-8 text-red-500 mr-3" />
                <div className="flex">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="h-5 w-5 text-yellow-400 fill-current" />
                  ))}
                </div>
              </div>
              
              <p className="text-gray-700 text-lg leading-relaxed mb-6 italic">
                "{testimonial.text}"
              </p>
              
              <div className="border-t pt-6">
                <p className="font-bold text-gray-900">{testimonial.name}</p>
                {/* <p className="text-red-600 font-semibold">{testimonial.business}</p> */}
              </div>
            </div>
          ))}
        </div>

        <img src="/image.png" alt="Rating Badges" className="w-[600px] h-auto mx-auto mb-16" />

        {/* Video Testimonials */}
        <div className="mb-16">
          <h3 className="text-3xl font-bold text-center text-black mb-12">
            Watch Our Clients Share Their Success Stories
          </h3>
          
          <div className="grid md:grid-cols-3 gap-8">
            {videoTestimonials.map((video, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                onClick={() => setActiveVideo(video.id)}
              >
                <div className="relative">
                  <img
                    src={video.thumbnail}
                    alt={video.title}
                    className="w-full h-48 object-cover"
                    onError={(e) => {
                      const target = e.target as HTMLImageElement;
                      target.src = `https://img.youtube.com/vi/${video.id}/hqdefault.jpg`;
                    }}
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-40 flex items-center justify-center">
                    <motion.div
                      whileHover={{ scale: 1.1 }}
                      className="bg-red-600 rounded-full p-4"
                    >
                      <Play className="h-8 w-8 text-white ml-1" />
                    </motion.div>
                  </div>
                </div>
                <div className="p-6">
                  <h4 className="text-xl font-bold text-black mb-2">{video.title}</h4>
                  <p className="text-red-600 font-semibold">{video.clientName}</p>
                  <p className="text-gray-600">{video.business}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Video Modal */}
        <AnimatePresence>
          {activeVideo && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-90 z-50 flex items-center justify-center p-4"
              onClick={() => setActiveVideo(null)}
            >
              <motion.div
                initial={{ scale: 0.5 }}
                animate={{ scale: 1 }}
                exit={{ scale: 0.5 }}
                className="relative max-w-4xl w-full"
                onClick={(e) => e.stopPropagation()}
              >
                <button
                  onClick={() => setActiveVideo(null)}
                  className="absolute -top-12 right-0 text-white hover:text-red-400 transition-colors"
                >
                  <X className="h-8 w-8" />
                </button>
                <div className="aspect-video bg-gray-900 rounded-lg overflow-hidden">
                  <iframe
                    src={`https://www.youtube.com/embed/${activeVideo}?autoplay=1&rel=0&modestbranding=1&origin=${window.location.origin}`}
                    title="Client Testimonial"
                    className="w-full h-full"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        <div className="text-center">
          <div className="bg-white p-8 rounded-xl shadow-lg inline-block">
            <div className="flex items-center justify-center mb-4">
              <div className="flex mr-4">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className={`h-6 w-6 ${i < 4 ? 'text-yellow-400 fill-current' : 'text-gray-300'}`} />
                ))}
              </div>
              <span className="text-2xl font-bold text-gray-900">4.7/5</span>
            </div>
            <p className="text-gray-600 text-lg">
              Average rating from <strong>600+ salon owners</strong> worldwide
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Testimonials;